import { z } from 'zod/v4'

import { jsonDocument } from '../../contract/index.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Conectores API» › the editor of one API template: the template is the subject; its saved versions, the attribute
 * catalogue and the state of each alias target asset are sections. The rules travel as open documents because their
 * shape is validated by the server when the template is saved. Each schema mirrors what the domain returns
 * (`toApiTemplateEditorTemplate`, `toApiTemplateVersionRow`, `toApiTemplateAttributeOption`, `classifyAliasTargets`) and
 * the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-api-template-editor-screen.type-assertions.ts` tie them.
 */

/**
 * The history is read up to here, the newest version first (`DEFAULT_TEMPLATE_VERSION_LIST_LIMIT`); beyond it the page
 * says «Se muestran N de M».
 */
export const ADMIN_API_TEMPLATE_VERSIONS_CAP = 50

const templateRuleDocument = jsonDocument({
  reason:
    'A pattern or transform rule of an API template: its shape is validated by the template routes on save and varies between generations of rules',
})

export const apiTemplateAssetAliasesSchema = z.record(z.string(), z.string())

/** What a template says and a saved version freezes: the snapshot of a version is exactly this body. */
const templateBodyShape = {
  exchangeSlug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  version: z.string(),
  patternRules: z.array(templateRuleDocument),
  transformRules: z.array(templateRuleDocument).nullable(),
  assetAliases: apiTemplateAssetAliasesSchema.nullable(),
  isActive: z.boolean(),
  isProductionReady: z.boolean(),
}

export const adminApiTemplateEditorTemplateSchema = z.strictObject({
  id: z.string(),
  ...templateBodyShape,
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const adminApiTemplateSnapshotSchema = z.strictObject(templateBodyShape)

export const adminApiTemplateVersionRowSchema = z.strictObject({
  versionId: z.string(),
  version: z.string(),
  createdAt: z.string(),
  /** The e-mail of the author, which the row paints; `null` when the user was deleted. */
  createdByDisplay: z.string().nullable(),
  notes: z.string().nullable(),
  snapshot: adminApiTemplateSnapshotSchema,
})

export const adminApiTemplateAttributeOptionSchema = z.strictObject({
  key: z.string(),
  category: z.string(),
  isActive: z.boolean(),
})

/** An active asset is `found`; only inactive ones, `inactive`; none, `missing`. There is no «unknown» state. */
export const adminAliasTargetStatusSchema = z.enum(['found', 'inactive', 'missing'])

export const adminAliasTargetCheckSchema = z.strictObject({
  /** In capitals; the lookup is exact and whole. */
  symbol: z.string(),
  status: adminAliasTargetStatusSchema,
})

export const adminApiTemplateEditorScreen = defineScreen({
  id: 'admin.apiImportMappings.templateEditor',
  path: '/api/screens/admin/api-import-mappings/template-editor',
  access: 'super_admin',
  query: z.strictObject({ id: z.guid() }),
  response: z.strictObject({
    /** The subject, first because it is the key that validates: an unknown id is the 404 of the WHOLE screen. */
    template: adminApiTemplateEditorTemplateSchema,
    versions: screenSection(
      z.strictObject({ rows: z.array(adminApiTemplateVersionRowSchema), total: z.number().int() }),
    ),
    attributes: screenSection(z.array(adminApiTemplateAttributeOptionSchema)),
    aliasTargets: screenSection(z.array(adminAliasTargetCheckSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.apiImportMappings', 'admin.apiImportMappings.detail'],
  budget: { requests: 1, statements: 18, bytes: 600_000, p95Ms: 400 },
  summary:
    'One API template as its editor edits it: its saved versions, the attribute catalogue and whether each alias target asset exists',
})

export type AdminApiTemplateEditorTemplate = z.infer<typeof adminApiTemplateEditorTemplateSchema>
export type AdminApiTemplateSnapshot = z.infer<typeof adminApiTemplateSnapshotSchema>
export type AdminApiTemplateVersionRow = z.infer<typeof adminApiTemplateVersionRowSchema>
export type AdminApiTemplateAttributeOption = z.infer<typeof adminApiTemplateAttributeOptionSchema>
export type AdminAliasTargetStatus = z.infer<typeof adminAliasTargetStatusSchema>
export type AdminAliasTargetCheck = z.infer<typeof adminAliasTargetCheckSchema>
export type AdminApiTemplateEditorData = ScreenData<typeof adminApiTemplateEditorScreen>
export type AdminApiTemplateEditorQuery = ScreenQuery<typeof adminApiTemplateEditorScreen>
